import { Edit } from '@refinedev/mui'
import {
  Box,
  Checkbox,
  FormControlLabel,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  DialogTitle,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  Typography,
  IconButton,
  Popover,
  Autocomplete,
} from '@mui/material'
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'
import { useForm } from '@refinedev/react-hook-form'
import { Controller } from 'react-hook-form'
import { useGetIdentity, useList } from '@refinedev/core'
import { User } from '@/utils/auth/auth0'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { useEffect, useRef, useState, useMemo } from 'react'
import { IwiData } from '@/interfaces/iwi'
import { getAsciiName } from '../../utils/textUtils'
import { getSixMonthsFromNow } from '@/utils/dataUtils'
import { EMAIL_REGEX } from '@/utils/validation'

interface FunderFormValues {
  id: number | undefined
  name: string
  type: string
  website: string
  description: string
  notes: string
  status: string
  active: boolean
  next_check_in_date: Date | null
  only_funds_registered_charities: boolean | null
  gambling_proceeds: boolean | null
  is_international: boolean | null
  iwi: number[]
  email_address: string | null
}

const formatDateForAPI = (date: Date | null): string | null => {
  if (!date) return null
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000)
    .toISOString()
    .split('T')[0]
}

export default function FunderEdit() {
  const {
    saveButtonProps,
    register,
    control,
    formState: { errors },
    refineCore: { query, onFinish },
    getValues,
    watch,
    setValue,
  } = useForm({
    refineCoreProps: {
      redirect: 'show',
    },
  })

  const user = useGetIdentity().data as User
  const [dateDialogOpen, setDateDialogOpen] = useState(false)
  const [helpAnchorEl, setHelpAnchorEl] = useState<HTMLElement | null>(null)

  const customFlagInputRef = useRef<HTMLInputElement>(null)

  const iwiValue = watch('iwi') || []

  const { data: iwiData, isLoading: iwiDataIsLoading } = useList({
    resource: 'iwi',
    config: {
      pagination: { current: 1, pageSize: 200 },
    },
    errorNotification: (error) => {
      return {
        message: 'Error getting iwi list',
        description:
          error?.message || 'An error occurred while fetching iwi list',
        type: 'error',
      }
    },
  })

  const iwiList = useMemo(
    () => (iwiData?.data as IwiData[]) || [],
    [iwiData?.data],
  )

  const selectedIwi = useMemo(() => {
    if (!iwiValue || !Array.isArray(iwiValue)) return []
    return iwiValue
      .map((iwiId: number) => iwiList.find((iwi) => iwi.id === iwiId))
      .filter((iwi): iwi is IwiData => iwi !== undefined)
  }, [iwiValue, iwiList])

  async function handleSave(values: FunderFormValues) {
    // Active funders require a check-in date.
    if (!values.next_check_in_date && values.active) {
      setDateDialogOpen(true)
      return
    }

    const updatedValues = {
      ...values,
      updated_by:
        user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
      next_check_in_date: !values.active
        ? null
        : formatDateForAPI(values.next_check_in_date),
    }

    await onFinish(updatedValues)
  }

  // handle setting date to 6 months from now and submitting
  function handleSetDateAndSubmit() {
    // set date to 6 months from now
    const sixMonthsFromNow = getSixMonthsFromNow()
    setValue('next_check_in_date', sixMonthsFromNow)

    setDateDialogOpen(false)

    const values = getValues() as FunderFormValues

    const updatedValues = {
      ...values,
      updated_by:
        user?.name || `${user?.given_name || ''} ${user?.family_name || ''}`,
      next_check_in_date: formatDateForAPI(sixMonthsFromNow),
    }

    onFinish(updatedValues)
  }

  useEffect(() => {
    if (query?.data?.data?.next_check_in_date) {
      setValue(
        'next_check_in_date',
        new Date(query.data.data.next_check_in_date), // Convert to Date object
      )
    }
  }, [query?.data?.data, setValue])

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        onClick: () => handleSave(getValues() as FunderFormValues),
      }}
    >
      <Box
        component="form"
        sx={{ display: 'flex', flexDirection: 'column' }}
        autoComplete="off"
      >
        <TextField
          {...register('name', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.name}
          helperText={(errors as any)?.name?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Name"
          name="name"
        />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Controller
            control={control}
            name="active"
            defaultValue={true}
            render={({ field }) => (
              <FormControlLabel
                label="Active"
                control={
                  <Checkbox
                    {...field}
                    checked={field.value}
                    onChange={(event) => {
                      field.onChange(event.target.checked)
                    }}
                  />
                }
              />
            )}
          />
          <IconButton
            size="small"
            onClick={(event) => setHelpAnchorEl(event.currentTarget)}
            sx={{ color: 'info.main' }}
          >
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
          <Popover
            open={Boolean(helpAnchorEl)}
            anchorEl={helpAnchorEl}
            onClose={() => setHelpAnchorEl(null)}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
          >
            <Box sx={{ p: 2, maxWidth: 400 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                Funder Status Guide:
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Active:</strong> Currently funding
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Inactive:</strong> Not currently funding, but may fund
                in the future - we should periodically check in (update next
                check-in date)
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                • <strong>Inactive + Notes:</strong> Not funding and never
                likely to fund in a way useful to us/our customers (explain why
                in Notes field)
              </Typography>
            </Box>
          </Popover>
        </Box>
        <Typography variant="body1">
          <span style={{ fontWeight: 600 }}>
            Please note: When updating the following fields, the Funders
            Opportunties will remain unchanged. Please update the Opportunities
            also if needed.
          </span>{' '}
          <br />
          This applies to - Gambling Proceeds, Is International, and Only Funds
          Registered Charities{' '}
        </Typography>
        <Controller
          control={control}
          name="only_funds_registered_charities"
          defaultValue={true}
          render={({ field }) => (
            <FormControlLabel
              label="Only funds registered charities"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked)
                  }}
                />
              }
            />
          )}
        />

        <Controller
          control={control}
          name="gambling_proceeds"
          defaultValue={true}
          render={({ field }) => (
            <FormControlLabel
              label="Gambling proceeds"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked)
                  }}
                />
              }
            />
          )}
        />
        <Controller
          control={control}
          name="is_international"
          defaultValue={true}
          render={({ field }) => (
            <FormControlLabel
              label="Is International"
              control={
                <Checkbox
                  {...field}
                  checked={field.value}
                  onChange={(event) => {
                    field.onChange(event.target.checked)
                  }}
                />
              }
            />
          )}
        />

        {!watch('active') && (
          <TextField
            {...register('status')}
            defaultValue={''}
            error={!!(errors as any)?.status}
            helperText={(errors as any)?.status?.message}
            margin="normal"
            fullWidth
            InputLabelProps={{ shrink: true }}
            type="text"
            label="Status (optional, as a description of why the funder is inactive)"
            name="status"
          />
        )}

        <Controller
          control={control}
          name="iwi"
          defaultValue={[]}
          render={({ field }) => (
            <Autocomplete
              multiple
              value={selectedIwi}
              sx={{ my: 3 }}
              onChange={(_event, newValues) => {
                const newIwi = newValues.map((value) => value.id)
                field.onChange(newIwi)
              }}
              options={iwiList}
              getOptionLabel={(option) => option.name}
              isOptionEqualToValue={(option, value) => option.id === value.id}
              filterOptions={(options, params) => {
                const inputValue = params.inputValue?.toLowerCase().trim()
                if (!inputValue) return []
                const asciiInput = getAsciiName(inputValue)

                return options.filter((option) => {
                  return getAsciiName(option.name).includes(asciiInput)
                })
              }}
              renderOption={(props, option) => (
                <li {...props} key={option.id}>
                  {option.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  label="Iwi"
                  {...params}
                  placeholder="Search iwi"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 1,
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: '#757575',
                    },
                  }}
                  InputProps={{
                    ...params.InputProps,
                    style: { backgroundColor: '#fff' },
                  }}
                />
              )}
            />
          )}
        />

        <Controller
          control={control}
          name="next_check_in_date"
          rules={{
            required: 'Next check-in date is required',
          }}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Next Check-in Date"
                value={field.value}
                onChange={(date: Date | null) => field.onChange(date)}
                format="dd/MM/yyyy"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    margin: 'normal',
                    required: true,
                    error: !!(errors as any)?.next_check_in_date,
                    helperText: (errors as any)?.next_check_in_date?.message,
                  },
                }}
              />
            </LocalizationProvider>
          )}
        />

        <TextField
          {...register('description', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.description}
          helperText={(errors as any)?.description?.message}
          margin="normal"
          fullWidth
          multiline
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Description"
          name="description"
        />
        <TextField
          {...register('type', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.type}
          helperText={(errors as any)?.type?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Type"
          name="type"
        />
        <TextField
          {...register('website', {
            required: 'This field is required',
          })}
          error={!!(errors as any)?.website}
          helperText={(errors as any)?.website?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="url"
          label="Website"
          name="website"
        />
        <TextField
          {...register('email_address', {
            setValueAs: (v: string) => v?.trim() || null,
            validate: (v) =>
              !v || EMAIL_REGEX.test(v) || 'Must be a valid email address',
          })}
          error={!!(errors as any)?.email_address}
          helperText={(errors as any)?.email_address?.message}
          margin="normal"
          fullWidth
          InputLabelProps={{ shrink: true }}
          type="email"
          label="Email Address"
          name="email_address"
        />

        <TextField
          {...register('notes')}
          error={!!(errors as any)?.notes}
          helperText={(errors as any)?.notes?.message}
          margin="normal"
          fullWidth
          multiline
          InputLabelProps={{ shrink: true }}
          type="text"
          label="Notes"
          name="notes"
        />
        <Dialog
          open={dateDialogOpen}
          onClose={() => setDateDialogOpen(false)}
          aria-labelledby="date-dialog-title"
          aria-describedby="date-dialog-description"
        >
          <DialogTitle id="date-dialog-title" sx={{ textAlign: 'center' }}>
            Missing Next Check-in Date
          </DialogTitle>
          <DialogContent sx={{ textAlign: 'center' }}>
            <DialogContentText id="date-dialog-description">
              The next check-in date is missing but is required. <br />
              Would you like to set the next check-in date to 6 months from now?
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDateDialogOpen(false)} color="primary">
              No
            </Button>
            <Button onClick={handleSetDateAndSubmit} color="primary" autoFocus>
              Yes
            </Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Edit>
  )
}
